import { useState } from 'react';
import { ThemeProvider, useTheme } from './ThemeContext';
import { Screen } from './components/BottomNavigation';
import BottomNavigation from './components/BottomNavigation';
import SplashScreen from './components/SplashScreen';
import ExpoSelectionScreen from './components/ExpoSelectionScreen';
import ExpoMapScreen from './components/ExpoMapScreen';
import AIRecommendationScreen from './components/AIRecommendationScreen';
import NotesScreen from './components/NotesScreen';
import ProfileScreen from './components/ProfileScreen';
import BoothDetailModal from './components/BoothDetailModal';
import ProgressScreen from './components/ProgressScreen';
import { Booth } from '../../mocks/boothData';
import { Expo } from '../../mocks/expoData';

type AppView = 'splash' | 'expo-select' | 'map' | 'ai' | 'notes' | 'profile' | 'progress';

const AppInner = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [view, setView] = useState<AppView>('splash');
  const [selectedExpo, setSelectedExpo] = useState<Expo | null>(null);
  const [selectedBooth, setSelectedBooth] = useState<Booth | null>(null);
  const [boothStates, setBoothStates] = useState<Record<string, Booth['status']>>({});

  const mainScreens: Screen[] = ['expo-select', 'map', 'ai', 'notes', 'profile'];
  const isMainView = mainScreens.includes(view as Screen);

  const handleExpoSelect = (expo: Expo) => {
    setSelectedExpo(expo);
    setView('map');
  };

  const handleMarkVisited = (id: string) => {
    setBoothStates(prev => ({
      ...prev,
      [id]: prev[id] === 'visited' ? 'not-visited' : 'visited',
    }));
  };

  const handleMarkImportant = (id: string) => {
    setBoothStates(prev => ({
      ...prev,
      [id]: prev[id] === 'important' ? 'not-visited' : 'important',
    }));
  };

  const navigate = (screen: Screen) => {
    setView(screen);
    setSelectedBooth(null);
  };

  return (
    <div
      className={`min-h-screen flex justify-center transition-colors duration-500 ${isDark ? 'bg-[#050508]' : 'bg-gray-200'}`}
    >
      {/* Mobile app container */}
      <div
        className={`relative w-full overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#08090F]' : 'bg-[#F8F9FB]'}`}
        style={{ maxWidth: '480px', minHeight: '100vh' }}
      >
        {/* Screen router */}
        {view === 'splash' && (
          <SplashScreen
            onEnter={() => setView('expo-select')}
            onDemo={() => { setView('map'); }}
          />
        )}

        {view === 'expo-select' && (
          <ExpoSelectionScreen
            onSelectExpo={handleExpoSelect}
            selectedExpoId={selectedExpo?.id ?? null}
          />
        )}

        {view === 'map' && (
          <ExpoMapScreen
            onBoothSelect={setSelectedBooth}
            boothStates={boothStates}
            onShowAI={() => setView('ai')}
            onShowProgress={() => setView('progress')}
            onShowNotes={() => setView('notes')}
          />
        )}

        {view === 'ai' && (
          <AIRecommendationScreen
            boothStates={boothStates}
            onBoothSelect={setSelectedBooth}
          />
        )}

        {view === 'notes' && (
          <NotesScreen />
        )}

        {view === 'profile' && (
          <ProfileScreen />
        )}

        {view === 'progress' && (
          <div className="relative">
            <ProgressScreen
              boothStates={boothStates}
              onBoothSelect={booth => {
                setSelectedBooth(booth);
                setView('map');
              }}
            />
            <button
              onClick={() => setView('map')}
              className={`fixed top-14 left-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#111827] text-gray-300 border border-[#1e2d47]' : 'bg-white text-gray-600 border border-gray-200'}`}
              style={{ maxWidth: '480px' }}
            >
              <i className="ri-arrow-left-line"></i> Back to Map
            </button>
          </div>
        )}

        {/* Bottom navigation (shown on main screens, not splash/progress) */}
        {isMainView && (
          <BottomNavigation
            activeScreen={view as Screen}
            onNavigate={navigate}
          />
        )}

        {/* Booth detail modal */}
        {selectedBooth && (
          <BoothDetailModal
            booth={selectedBooth}
            onClose={() => setSelectedBooth(null)}
            onMarkVisited={handleMarkVisited}
            onMarkImportant={handleMarkImportant}
          />
        )}
      </div>
    </div>
  );
};

const HomePage = () => (
  <ThemeProvider>
    <AppInner />
  </ThemeProvider>
);

export default HomePage;
